
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Coins, Loader2, AlertTriangle, Bug } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';

// DEV MODE SETTING - Set to true to bypass Stripe checkout during development
const DEV_MODE = true;

// Import the components with named imports to match how they're exported
import { CreditBalanceDisplay } from './CreditBalanceDisplay';
import { CreditPackageCard } from './CreditPackageCard';
import { CustomCreditPackage } from './CustomCreditPackage';
import { creditPackages } from './utils/creditUtils';

interface StewieCreditsPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export const StewieCreditsPopup = ({ isOpen, onClose }: StewieCreditsPopupProps) => {
  const [credits, setCredits] = useState<number>(0);
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPurchasing, setIsPurchasing] = useState<boolean>(false);
  const [isCustom, setIsCustom] = useState<boolean>(false);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [customPrice, setCustomPrice] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch user credits using a memoized callback
  const fetchCredits = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get User Session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user) {
        setError("User not authenticated.");
        setCredits(0);
        return;
      }

      const userId = session.user.id;

      // Fetch Credit Balance from real database
      const { data: creditData, error: creditError } = await supabase
        .from('user_credits')
        .select('balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (creditError) {
        if (creditError.code === '42P01') { 
          console.warn("`user_credits` table not found. Defaulting balance to 0.");
          setCredits(0);
        } else {
          throw creditError;
        }
      } else {
        setCredits(creditData?.balance || 0);
      }
    } catch (error: any) {
      setError(error.message || "Failed to load credit information");
      console.error('Error fetching credits:', error);
      toast({
        title: "Failed to load credits",
        description: error.message || "There was an error loading your credit information.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Call fetchCredits when the popup is opened
  useEffect(() => {
    if (isOpen) {
      fetchCredits();
      // Reset custom values when opening
      setIsCustom(false);
      setCustomAmount('');
      setSelectedPackage(null);
    }
  }, [isOpen, fetchCredits]);

  // Update custom price when custom amount changes
  useEffect(() => {
    // Parse the custom amount as a number, or default to 0 if invalid
    const amountValue = parseInt(customAmount.replace(/,/g, ''), 10) || 0;
    // Set the price (1:1 ratio for credits to dollars)
    setCustomPrice(amountValue);
  }, [customAmount]);

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers and commas
    const value = e.target.value.replace(/[^\d,]/g, '');
    setCustomAmount(value);
  };

  const handlePackageSelect = (packageId: string) => {
    setSelectedPackage(packageId);
    setIsCustom(false);
  };

  const selectCustomOption = () => {
    setSelectedPackage(null);
    setIsCustom(true);
  };

  const handlePurchase = async () => {
    if (!selectedPackage && !isCustom) return;
    if (isCustom && !customAmount) return;
    
    setIsPurchasing(true);
    setError(null);
    
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      if (!session?.user) throw new Error('User not authenticated');

      const userId = session.user.id;
      
      // Get credit amount from selected package or custom input
      let creditAmount = 0;
      let purchaseDescription = '';
      
      if (isCustom) {
        creditAmount = parseInt(customAmount.replace(/,/g, ''), 10) || 0;
        if (creditAmount <= 0) throw new Error("Invalid custom amount");
        purchaseDescription = 'Custom Package Purchase (Dev Mode)';
      } else if (selectedPackage) {
        const pkgDetails = creditPackages.find(pkg => pkg.id === selectedPackage);
        if (!pkgDetails) throw new Error("Invalid package selection");
        creditAmount = pkgDetails.amount;
        purchaseDescription = `${pkgDetails.name} Purchase (Dev Mode)`;
      } else {
        throw new Error("No package selected");
      }

      // DEVELOPMENT MODE - Bypass Stripe checkout and add credits directly
      if (DEV_MODE) {
        // 1. Check if user has a credit record
        const { data: existingCredit, error: creditError } = await supabase
          .from('user_credits')
          .select('balance')
          .eq('user_id', userId)
          .maybeSingle();
          
        if (creditError && creditError.code !== 'PGRST116') { // PGRST116 means no rows
          throw creditError;
        }
        
        // 2. Update or insert credit balance
        if (existingCredit) {
          // Update existing balance
          await supabase
            .from('user_credits')
            .update({ 
              balance: existingCredit.balance + creditAmount,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', userId);
        } else {
          // Insert new balance record
          await supabase
            .from('user_credits')
            .insert({ 
              user_id: userId, 
              balance: creditAmount,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
        }
        
        // 3. Record the transaction
        await supabase
          .from('credit_transactions')
          .insert({
            user_id: userId,
            type: 'purchase',
            amount: creditAmount,
            description: purchaseDescription,
            timestamp: new Date().toISOString(),
            metadata: { dev_mode: true }
          });
        
        // 4. Update local state and show success message
        const newBalance = (credits + creditAmount);
        setCredits(newBalance);
        toast({
          title: "Development Mode Purchase Successful",
          description: `Added ${creditAmount.toLocaleString()} credits to your account.`,
          variant: "success",
        });
        
        // Dispatch a custom event to notify other components
        window.dispatchEvent(new CustomEvent('creditBalanceUpdated', {
          detail: { newBalance: newBalance }
        }));
        
        // 5. Close the popup after a delay
        setTimeout(() => {
          onClose();
        }, 1500);
        
        return; // Skip the Stripe checkout flow
      }
      
      // PRODUCTION MODE - Normal Stripe checkout flow
      // Prepare payload for the edge function
      const payload: { priceId?: string; customAmount?: number; userId: string } = {
        userId: userId,
      };

      if (isCustom) {
        payload.customAmount = creditAmount;
      } else if (selectedPackage) {
        const pkgDetails = creditPackages.find(pkg => pkg.id === selectedPackage);
        if (!pkgDetails) throw new Error("Invalid package selection");
        
        if (pkgDetails.stripePriceId) {
          payload.priceId = pkgDetails.stripePriceId;
        } else {
          throw new Error("Invalid package selection for Stripe.");
        }
      }

      // Call the Supabase Edge Function to create a Stripe Checkout session
      // Use the full Supabase Edge Function URL
      const SUPABASE_URL = 'https://oyatwmwijwqgyhukqjux.supabase.co';
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = `Failed to create checkout session (HTTP ${response.status})`;
        try {
          // Safely try to parse the response as JSON
          const text = await response.text();
          console.log('Error response text:', text); // Log the raw response for debugging
          if (text) {
            try {
              const errorData = JSON.parse(text);
              if (errorData.error) {
                errorMessage = errorData.error;
              }
            } catch (jsonError) {
              // If JSON parsing fails, use the raw text if available
              if (text.length > 0) {
                errorMessage = `Server error: ${text}`;
              }
            }
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
        throw new Error(errorMessage);
      }

      // Safely parse the response
      let checkoutUrl = '';
      try {
        const text = await response.text();
        if (text) {
          const data = JSON.parse(text);
          checkoutUrl = data.url;
        }

        if (!checkoutUrl) {
          throw new Error('No checkout URL received from the server');
        }
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Invalid response from server');
      }

      if (checkoutUrl) {
        // Redirect the user to Stripe Checkout
        window.location.href = checkoutUrl;
      } else {
        throw new Error('No checkout URL received from the server.');
      }

    } catch (error: any) {
      const errorMsg = error.message || "There was an error processing your purchase. Please try again.";
      setError(errorMsg);
      toast({
        title: "Purchase Failed",
        description: errorMsg,
        variant: "destructive",
      });
      console.error('Purchase error:', error);
    } finally {
      setIsPurchasing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-yellow-500" />
            Stewie Credits
            {DEV_MODE && (
              <span className="text-xs font-normal px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full flex items-center ml-2">
                <Bug className="h-3 w-3 mr-1" />
                Dev Mode
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {DEV_MODE 
              ? "DEVELOPMENT MODE: Credits will be added directly to your account"
              : "Purchase credits to power your trading strategies"
            }
          </DialogDescription>
        </DialogHeader>
        
        {/* Error display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-4">
            <div className="flex items-start">
              <AlertTriangle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <p>{error}</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchCredits} 
              className="mt-2 text-xs h-7 px-2 py-1 w-full"
            >
              Retry
            </Button>
          </div>
        )}
        
        {/* Credits Display with Loading State */}
        {isLoading ? (
          <div className="animate-pulse space-y-2 p-4 rounded-lg bg-gray-50">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-8 w-48" />
          </div>
        ) : (
          <CreditBalanceDisplay credits={credits} />
        )}
        
        {/* Credit Packages */}
        <div className={`space-y-4 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
          {creditPackages.map((pkg) => (
            <CreditPackageCard
              key={pkg.id}
              id={pkg.id}
              name={pkg.name}
              amount={pkg.amount}
              price={pkg.price}
              popular={pkg.popular}
              isSelected={selectedPackage === pkg.id}
              onSelect={handlePackageSelect}
            />
          ))}
          
          {/* Custom Package Option */}
          <CustomCreditPackage
            isSelected={isCustom}
            onSelect={selectCustomOption}
            customAmount={customAmount}
            onCustomAmountChange={handleCustomAmountChange}
            customPrice={customPrice}
          />
        </div>
        
        <div className="mt-2 text-sm text-gray-500 italic text-center">
          Credits are used for advanced trading features
        </div>
        
        <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
          <Button variant="outline" onClick={onClose} className="sm:w-auto w-full">
            Cancel
          </Button>
          <Button 
            onClick={handlePurchase} 
            disabled={
              isLoading || 
              isPurchasing || 
              (isCustom && (!customAmount || parseInt(customAmount.replace(/,/g, ''), 10) <= 0)) || 
              (!isCustom && !selectedPackage)
            }
            className={`sm:w-auto w-full ${DEV_MODE ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
          >
            {isPurchasing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {DEV_MODE ? 'Add Credits (Dev Mode)' : 'Purchase Credits'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
